import { useEffect, useState } from 'react';
import axios from 'axios';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const R = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;
const label = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-ZA', { month: 'short', year: '2-digit' });

export const FleetSplitChart = ({ headers, fleetSel, refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState(12);
  useEffect(() => {
    axios.get(`${API}/fuel-tracker/fleet-split`, { headers, params: { months: range } }).then((r) => setRows(r.data)).catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, refreshKey]);
  const totals = rows.reduce((a, r) => ({ i: a.i + r.internal, e: a.e + r.external }), { i: 0, e: 0 });

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="fleet-split-chart">
      <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-slate-900 text-base">Internal vs External Fuel — Monthly</CardTitle>
        <div className="flex gap-1">
          {[6, 12, 24].map((n) => (
            <button key={n} onClick={() => setRange(n)} className={`text-xs px-2 py-1 rounded border ${range === n ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`} data-testid={`fleet-split-range-${n}`}>
              {n}m
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No fuel data yet.</p> : (
          <>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={label} tick={{ fontSize: 11, fill: '#475569' }} />
                  <YAxis tickFormatter={(v) => `R${Math.round(v / 1000)}k`} tick={{ fontSize: 11, fill: '#475569' }} width={48} />
                  <Tooltip formatter={(v, n) => [R(v), n]} labelFormatter={label} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {fleetSel.internal && <Bar dataKey="internal" name="Internal" stackId="f" fill="#059669" radius={fleetSel.external ? 0 : [4, 4, 0, 0]} />}
                  {fleetSel.external && <Bar dataKey="external" name="External" stackId="f" fill="#4f46e5" radius={[4, 4, 0, 0]} />}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-700" data-testid="fleet-split-totals">
              {fleetSel.internal && <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-600 mr-1" />Internal {R(totals.i)}</span>}
              {fleetSel.external && <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-indigo-600 mr-1" />External {R(totals.e)}</span>}
              {fleetSel.internal && fleetSel.external && <span className="text-slate-500">Internal share {totals.i + totals.e ? ((totals.i / (totals.i + totals.e)) * 100).toFixed(1) : 0}%</span>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
